import { useState } from 'react';
import { Activity, ArrowDownWideNarrow, ArrowDown10, ArrowUpNarrowWide, ChevronDown, Type } from 'lucide-react';
import { FilterOperator } from '@/comps/FilterPicker/filter-operators';
import { AvailableValue, VolumeKind } from '@/comps/FilterPicker/FilterPicker';
import { formatValue } from '@/comps/FilterPicker/utils';
import { QueryOrdering } from '@/api/generated/model';
import { CheckedState } from '@radix-ui/react-checkbox';
import { SearchInputV2 } from '@/comps/search-input';
import { translateSafe } from '@/utils/translations';
import { Popover, PopoverContent, PopoverTrigger } from '@/comps/primitives/popover';
import { List, ListItem } from '@/comps/list';
import { Button } from '@/comps/primitives/button';
import { Checkbox } from '@/comps/primitives/checkbox';
import { Input } from '@/comps/primitives/input';
import { Skeleton } from '@/comps/primitives/skeleton';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/comps/primitives/tooltip';
import { FILTER_CONTENT_V2_NESTED_POPOVER_CONTENT_CLASS } from '@/comps/FilterPicker/filter-content-v2/constants';
import { cn } from '@/utils/shad';
import { formatNumber } from '@/utils/format.util';

type Props = {
  filterKind: string;
  operator: FilterOperator;
  containsText: string;
  onContainsTextChange: (text: string) => void;
  onApplyContains: () => void;
  searchFilterValue: string;
  setSearchFilterValue: (v: string) => void;
  sortBy: QueryOrdering;
  onChangeSortBy: (by: QueryOrdering['by']) => void;
  onChangeSortByDirection: (by: QueryOrdering['by']) => void;
  availableFilterValues?: AvailableValue[];
  isLoading: boolean;
  targetFilterValues: string[];
  hasSelectedAll: boolean;
  showSelectAll: boolean;
  onCheckSelectAll: (checked: CheckedState) => void;
  onSelectFilterValue: (value: AvailableValue, checked: CheckedState) => void;
  volumeKind?: VolumeKind;
};

const SORT_OPTIONS: { value: QueryOrdering['by']; label: string }[] = [
  { value: 'n_interactions', label: translateSafe('variable', 'interactions') },
  { value: 'alphabetical', label: translateSafe('variable', 'alphabetical') },
];

export function FilterContentV2ValueDropdown({
  filterKind,
  operator,
  containsText,
  onContainsTextChange,
  onApplyContains,
  searchFilterValue,
  setSearchFilterValue,
  sortBy,
  onChangeSortBy,
  onChangeSortByDirection,
  availableFilterValues,
  isLoading,
  targetFilterValues,
  hasSelectedAll,
  showSelectAll,
  onCheckSelectAll,
  onSelectFilterValue,
  volumeKind,
}: Props) {
  if (operator === 'contains') {
    return (
      <div className="w-72 overflow-hidden rounded-md border border-zinc-800 bg-[#09090B] shadow-md">
        <div className="mb-3 flex items-center gap-2 px-3 pt-3">
          <Type className="h-4 w-4 shrink-0" />
          <Input
            className="h-5 border-0 bg-transparent px-0 pl-0.5 text-sm font-normal shadow-none focus-visible:ring-0"
            value={containsText}
            onChange={(e) => onContainsTextChange(e.currentTarget.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && containsText.trim()) {
                onApplyContains();
              }
            }}
          />
        </div>
        <div className="px-3 pb-3">
          <Button className="w-fit bg-primary" size="sm" disabled={!containsText.trim()} onClick={onApplyContains}>
            Apply filter
          </Button>
        </div>
      </div>
    );
  }

  const hasValues = !!availableFilterValues?.length;
  const showSearch = hasValues || !!searchFilterValue || isLoading;
  const showSortBy = hasValues || isLoading;
  const showEmpty = !isLoading && !targetFilterValues.length && !hasValues;

  return (
    <div className="flex max-h-72 w-72 flex-col overflow-hidden rounded-md border border-zinc-800 bg-[#09090B] shadow-md">
      {showSearch && (
        <SearchInputV2
          className="mb-4 px-3 pt-3"
          placeholder={translateSafe('common', 'searchEllipsis')}
          search={searchFilterValue}
          setSearch={(v) => setSearchFilterValue(v.toLowerCase())}
          searchInputClassNames={{
            container: 'h-auto w-full rounded-lg border-0 bg-[#09090B] p-0',
            inner: 'gap-2',
            leftIconContainer: 'h-4 w-4 rounded-none border-0 p-0',
            leftIcon: 'h-4 w-4',
            input:
              'h-5 bg-transparent py-0 pr-0 pl-0.5 text-sm placeholder:text-white font-normal shadow-none',
            spacer: 'hidden',
          }}
        />
      )}

      {showSortBy && (
        <div className="mb-2 border-b border-zinc-800 pb-3">
          <div className="flex items-center gap-2 px-3 py-0">
            <ArrowDown10 className="h-4 w-4 shrink-0" />
            <span className="text-sm text-foreground">{translateSafe('common', 'sortBy')}</span>
            <div className="ml-auto flex items-center gap-1">
              <SortByPill value={sortBy.by} onChange={onChangeSortBy} />
              <button
                type="button"
                className="flex h-7 w-7 shrink-0 items-center justify-center rounded-base border border-zinc-800 text-foreground hover:bg-zinc-900"
                onClick={() => onChangeSortByDirection(sortBy.by)}
                aria-label="Toggle sort direction"
              >
                {sortBy.direction === 'desc' ? (
                  <ArrowDownWideNarrow className="h-3.5 w-3.5" />
                ) : (
                  <ArrowUpNarrowWide className="h-3.5 w-3.5" />
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      <List
        className={cn('scrollbar mb-2 min-h-0 flex-1 overflow-y-auto px-1 text-[12px] leading-4', {
          'mb-0': showEmpty,
        })}
      >
        {isLoading ? (
          <ValueListSkeleton />
        ) : (
          <>
            {showSelectAll && (
              <ListItem className="my-0.5 flex w-full items-center justify-between gap-2 rounded-[8px] px-2.5 py-1.5 transition-colors ease-out hover:cursor-auto">
                <div className="flex w-full items-center gap-2.5">
                  <Checkbox
                    id="filter-value-select-all"
                    checked={hasSelectedAll}
                    onCheckedChange={onCheckSelectAll}
                    className="h-3 w-3 rounded-[2px] border-white! data-[state=checked]:text-white!"
                  />
                  <label
                    htmlFor="filter-value-select-all"
                    className="flex-1 cursor-pointer text-sm font-normal text-white"
                  >
                    {translateSafe('common', 'all')}
                  </label>
                </div>
              </ListItem>
            )}

            {showEmpty && (
              <ListItem className="my-0.5 flex w-full items-center justify-between rounded-[8px] px-2.5 py-1.5 transition-colors ease-out hover:cursor-auto">
                <Tooltip>
                  <TooltipTrigger className="flex-1" asChild>
                    <label
                      className="cursor-pointer truncate text-sm font-normal text-white"
                      title={translateSafe('common', 'noDataAvailable')}
                    >
                      {translateSafe('common', 'noDataAvailable')}
                    </label>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="text-start text-wrap">
                    {translateSafe('common', 'noDataAvailable')}
                  </TooltipContent>
                </Tooltip>
              </ListItem>
            )}

            {availableFilterValues?.map((availableFilterValue, i) => {
              const checked = targetFilterValues.includes(availableFilterValue.value) || hasSelectedAll || false;
              const id = `filter-value-${filterKind}-${i}`;

              return (
                <ListItem
                  key={i}
                  className="my-0.5 flex w-full items-center justify-between rounded-[8px] px-2.5 py-1.5 transition-colors ease-out hover:cursor-auto"
                >
                  <div className="flex w-full items-center gap-2.5">
                    <Checkbox
                      id={id}
                      checked={checked}
                      onCheckedChange={(c) => onSelectFilterValue(availableFilterValue, c)}
                      className="h-3 w-3 rounded-[2px] border-white! data-[state=checked]:text-white!"
                    />
                    <Tooltip>
                      <TooltipTrigger className="flex-1" asChild>
                        <label
                          htmlFor={id}
                          className="cursor-pointer truncate text-sm font-normal text-white"
                          title={availableFilterValue.value}
                        >
                          {formatValue(filterKind, availableFilterValue.value)}
                        </label>
                      </TooltipTrigger>
                      <TooltipContent side="top" className="text-start text-wrap">
                        {formatValue(filterKind, availableFilterValue.value)}
                      </TooltipContent>
                    </Tooltip>

                    {volumeKind && !!availableFilterValue.volume && (
                      <div className="ml-auto flex items-center gap-1 text-[10px] text-muted-foreground">
                        <Activity size={12} />
                        <span>{formatNumber(availableFilterValue.volume)}</span>
                      </div>
                    )}
                  </div>
                </ListItem>
              );
            })}
          </>
        )}
      </List>
    </div>
  );
}

function ValueListSkeleton() {
  return (
    <div className="flex flex-col gap-2 px-2.5 py-2">
      {Array.from({ length: 5 }).map((_, i) => (
        <div key={i} className="flex items-center gap-2.5">
          <Skeleton className="h-3 w-3 shrink-0 rounded-lg" />
          <Skeleton className="h-3 flex-1" />
        </div>
      ))}
    </div>
  );
}

function SortByPill({ value, onChange }: { value: QueryOrdering['by']; onChange: (by: QueryOrdering['by']) => void }) {
  const [open, setOpen] = useState(false);
  const selectedLabel = SORT_OPTIONS.find((o) => o.value === value)?.label ?? String(value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="flex h-7 max-w-full min-w-20 items-center justify-between gap-1 rounded-base border border-zinc-800 px-2 text-xs font-semibold"
        >
          <span className="max-w-full truncate">{selectedLabel}</span>
          <ChevronDown className="size-4 shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className={cn(FILTER_CONTENT_V2_NESTED_POPOVER_CONTENT_CLASS, 'w-45 border-zinc-800 bg-[#09090B] p-1')}
      >
        {SORT_OPTIONS.map((opt) => {
          const isSelected = opt.value === value;
          return (
            <ListItem
              key={opt.value}
              className={cn(
                'flex min-h-8 cursor-pointer items-center gap-2 rounded px-2 py-1.5 font-normal hover:bg-zinc-800',
                isSelected && 'bg-zinc-800',
              )}
              onClick={() => {
                onChange(opt.value);
                setOpen(false);
              }}
            >
              <span className="truncate text-sm font-normal text-foreground">{opt.label}</span>
            </ListItem>
          );
        })}
      </PopoverContent>
    </Popover>
  );
}
